import React, {
  useEffect, useState, useMemo, useRef,
} from 'react';
import * as Styles from './SliderImagesSection.styles';
import themeV2 from '@/themes/defaultThemeV2';
import { useViewport } from '@/hooks/useViewport';
import { Skeleton } from 'gatorcomponents';

const getPureBreakpointValue = value => parseInt(value.replace(/[^\d]/g, ''), 10);

const SliderImagesSection = ({ content, testId = 'slider-images-sections-component' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    imagesSliderLeft,
  } = content;

  const viewport = useViewport();
  const VISIBILITY_CONTENT_THRESHOLD_TO_DISPLAY = 0.4;
  const VISIBILITY_CONTENT_THRESHOLD_TO_DISPLAY_MOBILE = 0.6;

  const leftSliderRef = useRef(null);
  const sectionRef = useRef(null);

  const smBreakpointValue = getPureBreakpointValue(themeV2.breakpoints.md);
  const isMobile = viewport && viewport <= smBreakpointValue;

  const visibilityContentThreshold = useMemo(
    () => (isMobile
      ? VISIBILITY_CONTENT_THRESHOLD_TO_DISPLAY_MOBILE
      : VISIBILITY_CONTENT_THRESHOLD_TO_DISPLAY),
    [isMobile],
  );

  useEffect(() => {
    const hasAnimated = { current: false };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsLoading(true);

          leftSliderRef && leftSliderRef.current && leftSliderRef.current.classList.add('visible');

          setTimeout(() => {
            leftSliderRef && leftSliderRef.current && leftSliderRef.current.classList.add('start-scroll');
          }, 1000);
        }
      },
      { threshold: visibilityContentThreshold },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [visibilityContentThreshold]);

  const renderResponsiveImage = (image, index) => (
    <picture key={`${image.src}-${index}`}>
      <source media={`(max-width: ${themeV2.breakpoints.sm})`} srcSet={image.smallSrc} />
      <source media={`(max-width: ${themeV2.breakpoints.md})`} srcSet={image.midSrc} />
      <Styles.SliderImage
        loading="lazy"
        fetchPriority="low"
        alt={image.alt}
        src={image.src}
        $width={image.width}
        $midWidth={image.midWidth}
        $smallWidth={image.smallWidth}
      />
    </picture>
  );

  const imagesAtLeft = imagesSliderLeft.map(renderResponsiveImage);
  const repeatedLeftImages = Array(12)
    .fill(0)
    .flatMap((_, repetitionIndex) => imagesAtLeft.map(image => React.cloneElement(image, {
      key: `${image.key}-rep-${repetitionIndex}`,
    })));

  return (
    <Styles.Container ref={sectionRef} data-testid={testId}>
      {!isLoading && <Skeleton width="100%" height="215px" />}
      <Styles.Carousel>
        <Styles.AnimatedSlider ref={leftSliderRef} $direction="left">
          {repeatedLeftImages}
        </Styles.AnimatedSlider>
      </Styles.Carousel>
    </Styles.Container>
  );
};

export default React.memo(SliderImagesSection);
